import React, { FC } from 'react';
import { Translate } from 'react-localize-redux';
import { PulseLoader } from 'react-spinners';
import { Button } from 'reactstrap';

import classNames from '../../../helpers/classNames';
import useTranslate from '../../../hooks/useTranslate';
import { MappedOrderItemType, MappedOrderItemUserType } from '../../../services/apis/order/types';
import { MainUserError } from './types';

/**
 * Props for the Player component
 */
interface PlayerProps {
  player: MappedOrderItemUserType;
  isGolfhaftetRequest: boolean;
  isGitBooking: boolean;
  isThirdParty: boolean;
  mainUserError: MainUserError | null;
  isValidatingMainUser?: string | number | null;
  onClick: (player: MappedOrderItemUserType) => void;
  disabled: boolean;
}

/**
 * Renders a single player button with validation status
 */
const Player: FC<PlayerProps> = ({
  player,
  isGolfhaftetRequest,
  isGitBooking,
  isThirdParty,
  mainUserError,
  isValidatingMainUser,
  onClick,
  disabled,
}) => {
  const { translate } = useTranslate();

  const isInvalidMainUser =
    isGolfhaftetRequest && player.isLoggedInProfile && mainUserError?.playerId === player.id;

  const label =
    player.name || player.email || (player.firstName && `${player.firstName} ${player.lastName}`);

  return (
    <Button
      key={player.id || player.key}
      className={classNames(
        isInvalidMainUser ? 'invalid' : 'selected',
        'd-flex py-2 px-3 border mb-1 player'
      )}
      block
      color="link"
      onClick={() => onClick(player)}
      disabled={disabled}
      style={{ borderRadius: '8px' }}
    >
      {label}
      {isGitBooking && player.golfId && ` ${player.golfId}`}
      {isGolfhaftetRequest && player.ghCardNr && (
        <>
          {` - ${player.ghCardNr}`}
          {isValidatingMainUser === player.id && <PulseLoader size={10} className="ms-2" />}
        </>
      )}
      {player.price && ` (${player.price})`}
      {isInvalidMainUser && (
        <span className="ms-2">
          {translate('default.error', { error: mainUserError?.message })}
        </span>
      )}
    </Button>
  );
};

/**
 * Props for the PlayersInFlight component
 */
interface PlayersInFlightProps {
  item?: MappedOrderItemType;
  players: MappedOrderItemUserType[];
  playersInFlight: number;
  seatsInFlight?: number;
  flightFull?: boolean;
  removePlayer: (player: MappedOrderItemUserType) => void;
  isGolfhaftetRequest: boolean;
  isGitBooking: boolean;
  disabled: boolean;
  isThirdParty: boolean;
  isValidatingMainUser?: string | number | null;
  mainUserError: MainUserError | null;
}

/**
 * Displays a list of players in a flight with their validation status
 */
export const PlayersInFlight: FC<PlayersInFlightProps> = ({
  players,
  playersInFlight,
  seatsInFlight,
  removePlayer,
  isGolfhaftetRequest,
  isGitBooking,
  disabled,
  isThirdParty,
  isValidatingMainUser,
  mainUserError,
}) => {
  const onPlayerClick = (player: MappedOrderItemUserType): void => {
    // Only in Golfamore bookings is it possible to remove yourself from the flight
    if (!isGolfhaftetRequest && player.isLoggedInProfile && !isThirdParty) return;
    removePlayer(player);
  };

  return (
    <>
      <h4 className="my-3 fw-bold">
        <Translate id="default.BookingPlayers" /> ({playersInFlight}/{seatsInFlight || 0})
      </h4>
      {players?.map(player => (
        <Player
          key={player.id || player.key}
          player={player}
          onClick={onPlayerClick}
          isGolfhaftetRequest={isGolfhaftetRequest}
          isGitBooking={isGitBooking}
          isThirdParty={isThirdParty}
          isValidatingMainUser={isValidatingMainUser}
          mainUserError={mainUserError}
          disabled={disabled}
        />
      ))}
    </>
  );
};
